/**
 * Feature Flag Service
 * Fetches feature flags from backend API for remote control
 *
 * This enables centralized feature flag management where:
 * - Backend reads from Vercel environment variables
 * - Frontend fetches current values from backend API
 * - All users get consistent flag values (no localStorage drift)
 */

export interface RemoteFeatureFlag {
  key: string;
  label: string;
  description?: string;
  value: boolean;
  scope: 'env' | 'user';
}

interface FetchFlagsResponse {
  flags: RemoteFeatureFlag[];
}

// Get API base URL from environment
const getApiBaseUrl = (): string => {
  // Check if we're in development (Vite dev server)
  const isDev = import.meta.env.DEV;

  if (isDev) {
    // Use VITE_API_URL for local development
    return import.meta.env.VITE_API_URL || 'http://localhost:3001';
  }

  // In production, use relative URL (same origin)
  return '';
};

/**
 * Fetch feature flags from backend (admin-authenticated)
 * @returns Promise<Map<string, boolean>> - Map of flag keys to values
 */
export async function fetchFeatureFlags(authToken?: string): Promise<Map<string, boolean>> {
  const apiBaseUrl = getApiBaseUrl();
  const endpoint = `${apiBaseUrl}/api/admin/flags`;

  try {
    console.log('[FeatureFlagService] Fetching flags from:', endpoint);

    const response = await fetch(endpoint, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        ...(authToken ? { Authorization: `Bearer ${authToken}` } : {})
      }
    });

    if (!response.ok) {
      throw new Error(`HTTP ${response.status}: ${response.statusText}`);
    }

    const data: FetchFlagsResponse = await response.json();
    const flags = new Map<string, boolean>();

    data.flags.forEach((flag: RemoteFeatureFlag) => {
      flags.set(flag.key, flag.value);
    });

    console.log('[FeatureFlagService] Successfully fetched flags:', Object.fromEntries(flags));
    return flags;
  } catch (error) {
    console.error('[FeatureFlagService] Failed to fetch flags from backend:', error);
    // Return empty map on error - will fall back to defaults in context
    return new Map();
  }
}

/**
 * Fetch public feature flags (optional auth for per-user overrides)
 * This endpoint is for all users to get current flag values
 * @param authToken - Optional Firebase auth token for user-specific overrides
 * @returns Promise<Map<string, boolean>> - Map of flag keys to values
 */
export async function fetchPublicFeatureFlags(authToken?: string): Promise<Map<string, boolean>> {
  const apiBaseUrl = getApiBaseUrl();
  const endpoint = `${apiBaseUrl}/api/flags`; // Note: no "admin" in path

  try {
    console.log('[FeatureFlagService] Fetching public flags from:', endpoint);

    const response = await fetch(endpoint, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        ...(authToken ? { Authorization: `Bearer ${authToken}` } : {})
      }
    });

    if (!response.ok) {
      throw new Error(`HTTP ${response.status}: ${response.statusText}`);
    }

    const data = await response.json();
    const flags = new Map<string, boolean>();

    // Convert object to Map
    if (data.flags && typeof data.flags === 'object') {
      Object.entries(data.flags).forEach(([key, value]) => {
        flags.set(key, value as boolean);
      });
    }

    console.log('[FeatureFlagService] Successfully fetched public flags:', Object.fromEntries(flags));
    return flags;
  } catch (error) {
    console.error('[FeatureFlagService] Failed to fetch public flags:', error);
    // Return empty map on error - will fall back to defaults in context
    return new Map();
  }
}

/**
 * Update a feature flag value (admin only)
 * This updates the runtime flag, but for permanent changes you need to update Vercel env vars
 *
 * @param key - Flag key to update
 * @param value - New boolean value
 * @param authToken - Admin authentication token
 */
export async function updateFeatureFlag(
  key: string,
  value: boolean,
  authToken: string
): Promise<void> {
  const apiBaseUrl = getApiBaseUrl();
  const endpoint = `${apiBaseUrl}/api/admin/flags`;

  console.log(`[FeatureFlagService] Updating flag ${key} to ${value}`);

  const response = await fetch(endpoint, {
    method: 'PATCH',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${authToken}`
    },
    body: JSON.stringify({ key, value })
  });

  if (!response.ok) {
    const errorText = await response.text();
    throw new Error(`Failed to update flag: ${response.status} - ${errorText}`);
  }

  const result = await response.json();
  console.log('[FeatureFlagService] Flag updated:', result);
}

/**
 * Set Vercel environment variable via API (requires Vercel token)
 * This is for permanent flag changes that persist across deployments
 *
 * @param key - Environment variable key (e.g., FEATURE_FLAG_RESULTS_NEW_DESIGN)
 * @param value - Value to set
 * @param vercelToken - Vercel API token
 * @param projectId - Vercel project ID
 */
export async function setVercelEnvVar(
  key: string,
  value: string,
  vercelToken: string,
  projectId: string
): Promise<void> {
  console.log(`[FeatureFlagService] Setting Vercel env var ${key} to ${value}`);

  // Vercel API endpoint for environment variables
  const endpoint = `https://api.vercel.com/v9/projects/${projectId}/env`;

  try {
    // First, try to get existing env var
    const getResponse = await fetch(`${endpoint}?key=${key}`, {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${vercelToken}`,
        'Content-Type': 'application/json'
      }
    });

    if (getResponse.ok) {
      // Env var exists, delete it first
      const existing = await getResponse.json();
      if (existing.envs && existing.envs.length > 0) {
        const envId = existing.envs[0].id;
        await fetch(`${endpoint}/${envId}`, {
          method: 'DELETE',
          headers: {
            'Authorization': `Bearer ${vercelToken}`
          }
        });
      }
    }

    // Create new env var
    const response = await fetch(endpoint, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${vercelToken}`,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({
        key,
        value,
        type: 'encrypted',
        target: ['production', 'preview', 'development']
      })
    });

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`Failed to set Vercel env var: ${response.status} - ${errorText}`);
    }

    console.log('[FeatureFlagService] Vercel env var set successfully');
  } catch (error) {
    console.error('[FeatureFlagService] Failed to set Vercel env var:', error);
    throw error;
  }
}
